import { addDays, addWeeks, addMonths, addYears, isSameMonth, getDate } from "date-fns";

// ── Frequency helpers ──────────────────────────────────────────────────────────

/**
 * Convert an amount to its monthly equivalent based on frequency.
 */
export function toMonthly(amount, frequency) {
  const n = parseFloat(amount) || 0;
  switch (frequency) {
    case "weekly":       return (n * 52) / 12;
    case "bi-weekly":    return (n * 26) / 12;
    case "semi-monthly": return n * 2;
    case "monthly":      return n;
    case "annual":       return n / 12;
    case "one-time":     return n; // treat as one-time this month
    default:             return n;
  }
}

// ── Summary calculations ───────────────────────────────────────────────────────

export function calcMonthlyIncome(paychecks) {
  return paychecks.reduce((sum, p) => sum + toMonthly(p.amount, p.frequency), 0);
}

export function calcMonthlyBills(bills) {
  return bills.reduce((sum, b) => sum + toMonthly(b.amount, b.frequency), 0);
}

export function calcTotalSavings(goals) {
  return goals.reduce((sum, g) => sum + (g.currentAmount || 0), 0);
}

export function calcTotalDebt(debts) {
  return debts.reduce((sum, d) => sum + (d.balance || 0), 0);
}

export function calcLeftover(paychecks, bills) {
  return calcMonthlyIncome(paychecks) - calcMonthlyBills(bills);
}

// ── Debt payoff projection ─────────────────────────────────────────────────────

/**
 * Simulate debt payoff for a given strategy and optional extra monthly payment.
 * Returns { months, totalInterest, projectedDate, debtDetails }
 * where debtDetails is an array of { id, name, months, totalInterest }.
 */
export function calcDebtPayoff(debts, strategy = "avalanche", extraPayment = 0) {
  if (!debts.length) return { months: 0, totalInterest: 0, projectedDate: null, debtDetails: [] };

  // Clone and sort
  let sorted = debts.map((d) => ({
    id: d.id,
    name: d.name,
    balance: parseFloat(d.balance) || 0,
    minPayment: parseFloat(d.minimumPayment) || 0,
    apr: parseFloat(d.apr) || 0,
    paidOffMonth: null,
  }));

  if (strategy === "avalanche") {
    sorted.sort((a, b) => b.apr - a.apr);
  } else {
    sorted.sort((a, b) => a.balance - b.balance);
  }

  let month = 0;
  let totalInterest = 0;

  while (sorted.some((d) => d.balance > 0.01) && month < 600) {
    month++;

    // 1. Apply monthly interest
    sorted.forEach((d) => {
      if (d.balance > 0) {
        const interest = d.balance * (d.apr / 100 / 12);
        totalInterest += interest;
        d.balance += interest;
      }
    });

    // 2. Pay minimums; collect freed-up payments from paid-off debts
    let freed = 0;
    sorted.forEach((d) => {
      if (d.balance > 0) {
        const payment = Math.min(d.minPayment, d.balance);
        d.balance -= payment;
        if (d.balance < 0.01) {
          d.balance = 0;
          if (d.paidOffMonth === null) {
            d.paidOffMonth = month;
            freed += d.minPayment; // snowball freed payment
          }
        }
      }
    });

    // 3. Apply extra + freed payments to the first remaining unpaid debt
    let extra = extraPayment + freed;
    for (const d of sorted) {
      if (d.balance > 0.01 && extra > 0) {
        const payment = Math.min(extra, d.balance);
        d.balance -= payment;
        extra -= payment;
        if (d.balance < 0.01) {
          d.balance = 0;
          if (d.paidOffMonth === null) d.paidOffMonth = month;
        }
        break;
      }
    }
  }

  const projectedDate = new Date();
  projectedDate.setMonth(projectedDate.getMonth() + month);

  const debtDetails = sorted.map((d) => ({
    id: d.id,
    name: d.name,
    months: d.paidOffMonth || month,
  }));

  return { months: month, totalInterest, projectedDate, debtDetails };
}

// ── Calendar event generation ──────────────────────────────────────────────────

/**
 * Returns an array of { date: Date, type, label, color, amount } objects
 * for all events that fall within the given year/month.
 */
export function getMonthEvents(year, month, paychecks, bills, savingsGoals) {
  const events = [];
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();

  // ── Paycheck events ──
  paychecks.forEach((p) => {
    const start = p.startDate ? new Date(p.startDate) : new Date();
    const dates = getFrequencyDatesInMonth(start, p.frequency, year, month, {
      semiMonthlyDay1: p.semiMonthlyDay1,
      semiMonthlyDay2: p.semiMonthlyDay2,
    });
    dates.forEach((date) => {
      events.push({
        date,
        type: "paycheck",
        label: p.label || p.spouseName || "Paycheck",
        color: "blue",
        amount: p.amount,
      });
    });
  });

  // ── Bill events ──
  bills.forEach((b) => {
    if (!b.dueDate) return;
    const due = new Date(b.dueDate);
    const dates = getFrequencyDatesInMonth(due, b.frequency, year, month);
    dates.forEach((date) => {
      events.push({
        date,
        type: "bill",
        label: b.name,
        color: categoryColor(b.category),
        amount: -b.amount,
        isPaid: b.isPaid,
        category: b.category,
      });
    });
  });

  return events.sort((a, b) => a.date - b.date);
}

function getFrequencyDatesInMonth(baseDate, frequency, year, month, options = {}) {
  const results = [];
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  switch (frequency) {
    case "monthly":
    case "one-time": {
      const d = new Date(year, month, baseDate.getDate());
      if (d >= firstDay && d <= lastDay) results.push(d);
      break;
    }
    case "semi-monthly": {
      const day1 = parseInt(options.semiMonthlyDay1) || 1;
      const day2 = parseInt(options.semiMonthlyDay2) || 15;
      [day1, day2].forEach((day) => {
        const d = new Date(year, month, day);
        if (d >= firstDay && d <= lastDay) results.push(d);
      });
      break;
    }
    case "weekly": {
      let cursor = new Date(baseDate);
      // Advance to first occurrence in or after firstDay
      while (cursor < firstDay) cursor = addWeeks(cursor, 1);
      while (cursor <= lastDay) {
        results.push(new Date(cursor));
        cursor = addWeeks(cursor, 1);
      }
      break;
    }
    case "bi-weekly": {
      let cursor = new Date(baseDate);
      while (cursor < firstDay) cursor = addDays(cursor, 14);
      while (cursor <= lastDay) {
        results.push(new Date(cursor));
        cursor = addDays(cursor, 14);
      }
      break;
    }
    case "annual": {
      const d = new Date(year, baseDate.getMonth(), baseDate.getDate());
      if (d >= firstDay && d <= lastDay) results.push(d);
      break;
    }
    default:
      break;
  }
  return results;
}

export function categoryColor(category) {
  const map = {
    Housing: "purple",
    Utilities: "yellow",
    Subscriptions: "pink",
    Transportation: "orange",
    Groceries: "green",
    Insurance: "teal",
    Entertainment: "indigo",
    Medical: "red",
    Other: "gray",
  };
  return map[category] || "gray";
}

// ── Weekly cash-flow breakdown ─────────────────────────────────────────────────

/**
 * Split month events into ISO weeks and compute income vs expense per week.
 */
export function getWeeklyCashFlow(events) {
  const weeks = {};
  events.forEach((ev) => {
    const d = ev.date;
    // Use Monday as week start; build a key like "Apr 14"
    const weekStart = new Date(d);
    weekStart.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // Mon
    const key = weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    if (!weeks[key]) weeks[key] = { label: key, income: 0, expenses: 0, start: weekStart };
    if (ev.amount > 0) weeks[key].income += ev.amount;
    else weeks[key].expenses += Math.abs(ev.amount);
  });
  return Object.values(weeks).sort((a, b) => a.start - b.start);
}

// ── Formatting helpers ─────────────────────────────────────────────────────────

export function fmt(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);
}
